import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Grid, CircularProgress, Alert, Button, 
    Divider, IconButton, Dialog, DialogTitle, DialogContent, 
    DialogActions, TextField, Breadcrumbs, Link
} from '@mui/material';
import { 
    AccountBalance, ArrowBack, Edit, ContentCopy, 
    CheckCircleOutline, InfoOutlined 
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getVendorBankDetails, upsertVendorBankDetails } from '../../services/Api';

const ViewVendorBankDetails = () => {
    const { vendorId } = useParams();
    const navigate = useNavigate();
    
    const [bankDetails, setBankDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        accountNumber: '',
        bankName: '',
        ifscCode: '',
        accountHolderName: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch bank details directly
            const bankResp = await getVendorBankDetails(vendorId);
            if (bankResp.success) {
                setBankDetails(bankResp.data);
                if (bankResp.data) {
                    setFormData({
                        accountNumber: bankResp.data.accountNumber || '',
                        bankName: bankResp.data.bankName || '',
                        ifscCode: bankResp.data.ifscCode || '',
                        accountHolderName: bankResp.data.accountHolderName || ''
                    });
                }
            } else {
                setBankDetails(null);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setBankDetails(null);
            } else {
                setError(err.response?.data?.message || err.message || 'An error occurred while fetching bank details');
                console.error(err);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    const handleOpenModal = () => {
        setFormError(null);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'accountHolderName' || name === 'bankName') {
            // Only allow letters and spaces
            if (!/^[a-zA-Z\s]*$/.test(value)) {
                return;
            }
        }
        
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.accountNumber || !formData.bankName || !formData.ifscCode || !formData.accountHolderName) {
            setFormError('All fields are required');
            return;
        }

        if (!/^[a-zA-Z\s]*$/.test(formData.accountHolderName)) {
            setFormError('Account holder name can only contain letters and spaces');
            return;
        }

        if (!/^[a-zA-Z\s]*$/.test(formData.bankName)) {
            setFormError('Bank name can only contain letters and spaces');
            return;
        }

        setSubmitting(true);
        setFormError(null);
        try {
            const response = await upsertVendorBankDetails(vendorId, formData);
            if (response.success) {
                setModalOpen(false);
                fetchData();
            } else {
                setFormError(response.message || 'Failed to update bank details');
            }
        } catch (err) {
            setFormError(err.response?.data?.message || 'An error occurred while updating bank details');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const DetailRow = ({ label, value, copyable = false }) => (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: copyable ? 'monospace' : 'inherit' }}>
                    {value || 'Not Provided'}
                </Typography>
                {copyable && value && (
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(value)}>
                        <ContentCopy fontSize="inherit" />
                    </IconButton>
                )}
            </Grid>
        </Grid>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/vendors/food')} sx={{ cursor: 'pointer' }}>
                        Food Vendors
                    </Link>
                    <Typography color="text.primary">Bank Details</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center' }}>
                            <AccountBalance sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                            Bank Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Managing bank records for this vendor
                        </Typography>
                    </Box>
                    <Button 
                        startIcon={<ArrowBack />} 
                        onClick={() => navigate('/vendors/food')}
                        variant="outlined"
                        sx={{ borderRadius: '8px' }}
                    >
                        Back to List
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            {!bankDetails ? (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                    <InfoOutlined sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>No Bank Details Found</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        This vendor has not registered any bank information yet.
                    </Typography>
                    <Button variant="contained" startIcon={<Edit />} onClick={handleOpenModal}>
                        Add Bank Details
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <Paper sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Settlement Account</Typography>
                                <Button startIcon={<Edit />} variant="outlined" size="small" onClick={handleOpenModal}>
                                    Update
                                </Button>
                            </Box>
                            <Divider sx={{ mb: 4 }} />
                            
                            <DetailRow label="Account Holder Name" value={bankDetails.accountHolderName} />
                            <DetailRow label="Account Number" value={bankDetails.accountNumber} copyable />
                            <DetailRow label="Bank Name" value={bankDetails.bankName} />
                            <DetailRow label="IFSC Code" value={bankDetails.ifscCode} copyable />
                            
                            <Box sx={{ mt: 4, p: 2, backgroundColor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <CheckCircleOutline color="success" fontSize="small" />
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                    This account is currently active for settlement.
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Update Modal */}
            <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>{bankDetails ? 'Update' : 'Add'} Bank Details</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Please enter the correct banking information.
                    </Typography>
                    
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Account Holder Name" fullWidth size="small" name="accountHolderName"
                                value={formData.accountHolderName} onChange={handleInputChange} autoFocus
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Account Number" fullWidth size="small" name="accountNumber"
                                value={formData.accountNumber} onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Bank Name" fullWidth size="small" name="bankName"
                                value={formData.bankName} onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="IFSC Code" fullWidth size="small" name="ifscCode"
                                value={formData.ifscCode} onChange={handleInputChange}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={handleCloseModal} color="inherit">Cancel</Button>
                    <Button 
                        onClick={handleSubmit} variant="contained" disabled={submitting}
                        sx={{ px: 4 }}
                    >
                        {submitting ? 'Saving...' : 'Save Details'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ViewVendorBankDetails;
